import type { OrchestrationThreadActivity } from "@amoreg/contracts";
import type { ContextWindowSnapshot } from "@amoreg/shared/contextWindow";
import type { ThreadShell } from "../../types";

/** Reserve the persisted usage meter's footprint until the thread detail arrives. */
export function shouldReserveContextWindowMeter(input: {
  detailLoading: boolean;
  shell: Pick<ThreadShell, "latestTurn" | "latestUserMessageAt" | "session"> | null | undefined;
}): boolean {
  const { shell } = input;
  return (
    input.detailLoading &&
    Boolean(
      shell &&
      (shell.latestTurn !== null || shell.latestUserMessageAt !== null || shell.session !== null),
    )
  );
}

/**
 * The provider compacts on request: its snapshot lists the `/compact`
 * command. Drives both the composer's slash menu entry (listed from the same
 * snapshot) and the meter's "Compact context" button.
 */
export function providerSupportsManualCompaction(
  provider: { readonly slashCommands: ReadonlyArray<{ readonly name: string }> } | null | undefined,
): boolean {
  return provider?.slashCommands.some((command) => command.name === "compact") ?? false;
}

/** The meter's reading, with what a compaction since it changed. */
export type CompactionAwareContextWindow = ContextWindowSnapshot & {
  /**
   * The context was compacted after this reading and the provider has not
   * measured the smaller context yet (a manual compaction; the next reply
   * measures it). The used count no longer describes the context.
   */
  readonly awaitingCompactedUsage: boolean;
};

/**
 * Refreshes the meter after a compaction: a divider newer than the latest
 * usage reading that carries the compacted size replaces the used count with
 * it; one that does not yet marks the reading stale instead of leaving the
 * pre-compaction count on screen.
 */
export function reflectCompactionInContextWindow(
  snapshot: ContextWindowSnapshot | null,
  activities: ReadonlyArray<Pick<OrchestrationThreadActivity, "kind" | "payload" | "createdAt">>,
): CompactionAwareContextWindow | null {
  if (snapshot === null) return null;
  const compaction = activities.findLast((activity) => activity.kind === "context-compaction");
  if (compaction === undefined || compaction.createdAt <= snapshot.updatedAt) {
    return { ...snapshot, awaitingCompactedUsage: false };
  }
  const payload =
    compaction.payload !== null && typeof compaction.payload === "object"
      ? (compaction.payload as { readonly afterTokens?: unknown })
      : {};
  const afterTokens =
    typeof payload.afterTokens === "number" && Number.isFinite(payload.afterTokens)
      ? payload.afterTokens
      : null;
  if (afterTokens === null) {
    return { ...snapshot, awaitingCompactedUsage: true };
  }
  const maxTokens = typeof snapshot.maxTokens === "number" ? snapshot.maxTokens : null;
  const usedPercentage =
    maxTokens !== null && maxTokens > 0 ? Math.min(100, (afterTokens / maxTokens) * 100) : null;
  return {
    ...snapshot,
    usedTokens: afterTokens,
    usedPercentage,
    remainingTokens: maxTokens !== null ? Math.max(0, Math.round(maxTokens - afterTokens)) : null,
    remainingPercentage: usedPercentage !== null ? Math.max(0, 100 - usedPercentage) : null,
    updatedAt: compaction.createdAt,
    awaitingCompactedUsage: false,
  };
}

/**
 * Why "Compact context" cannot run right now, or null when it can. It sends
 * a turn like any message, so it waits for the same things a send does.
 */
export function compactContextDisabledReason(input: {
  readonly running: boolean;
  readonly connecting: boolean;
  readonly sendBusy: boolean;
  readonly environmentUnavailable: boolean;
  /** The thread's messages are still loading (a send waits for them too). */
  readonly detailLoading?: boolean;
}): string | null {
  if (input.environmentUnavailable) return "Available once the connection is back";
  if (input.detailLoading === true) return "Available once the messages load";
  if (input.running || input.connecting) return "Available once the current response finishes";
  if (input.sendBusy) return "Sending…";
  return null;
}
